import React, { useState, useEffect, useContext }  from 'react';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import LayoutNavSectionCombinationList from 'components/layout_and_quote_builder/LayoutNavSectionCombinationList.jsx';
import SelectedList from 'components/layout_and_quote_builder/quote_builder/SelectedList.jsx';
import LayoutContext from 'components/contexts/LayoutContext.jsx';
import ChooseLayoutDimensions from 'components/layout_and_quote_builder/options/ChooseLayoutDimensions.jsx';
import ManufacturingLineLists from 'components/frank/ManufacturingLineLists.jsx';
import GeneralNavSection from 'components/layout_and_quote_builder/quote_builder/GeneralNavSection.jsx';
import { shouldShowSectionLabel } from 'components/layout_and_quote_builder/utils/navSectionUtils.js';

const LayoutAndQuoteBuilderNavSection = ( props ) => {
  const myLayoutContext = useContext(LayoutContext);

  // const fallbackMaterialValues = () => {
  //   return {
  //     layout_dimension_ids: [21],
  //     layout_dimension_unit: myLayoutContext.layoutOptions.layout_dimension_unit,
  //     layout_dimension_top_margin_diecutting: myLayoutContext.layoutOptions.layout_dimension_top_margin,
  //     layout_dimension_side_margin_diecutting: myLayoutContext.layoutOptions.layout_dimension_side_margin,
  //     layout_dimension_bottom_margin_diecutting: myLayoutContext.layoutOptions.layout_dimension_bottom_margin,

  //     layout_dimension_top_margin_printing: myLayoutContext.layoutOptions.layout_dimension_top_margin,
  //     layout_dimension_side_margin_printing: myLayoutContext.layoutOptions.layout_dimension_side_margin,
  //     layout_dimension_bottom_margin_printing: myLayoutContext.layoutOptions.layout_dimension_bottom_margin,
  //   }
  // }

  // Use props if provided, otherwise use local state (for backwards compatibility)
  const selectedList = props.selectedList !== undefined ? props.selectedList : [];
  const setSelectedList = props.setSelectedList || (() => {});

  const [materialLayoutDimensions, setMaterialLayoutDimensions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // The bobbin checkbox value, kept here because it is an input to BOTH displays below it: the "Chosen
  // material dimensions" and (in Frank mode) the manufacturing lines found over those same sheets. Undefined
  // until the user touches the checkbox -> the part_type default.
  const [bobbinEnabled, setBobbinEnabled] = useState(undefined);

  // The bobbin checkbox is an input to the material-sheet finder, so re-fetching with the toggle value
  // recomputes "Chosen material dimensions" (bobbin true/false changes the result). Absent on mount ->
  // the part_type default.
  const fetchDefaultMaterialValues = async (bobbinEnabled) => {
    if(props.navSection.index == undefined) {
      return ;
    }
    try {
      const bobbinQuery = bobbinEnabled === undefined ? '' : `?bobbin_enabled=${bobbinEnabled}`;
      // Frank mode (the /mcc page) computes the "Chosen material dimensions" through
      // Frank::SectionManufacturingLineFinderSheets via Frank's own endpoint; the legacy page keeps
      // LayoutCombinationsFinder::DefaultMaterialValues.
      const materialSheetsUrl = window.frank_mode
        ? `/mcc/${window.user_quote_id}/section-material-sheets/${props.navSection.index}${bobbinQuery}`
        : `/manufacturing-cost-calculator/${window.user_quote_id}/default-material-values/${props.navSection.index}${bobbinQuery}`;
      const response = await fetch(materialSheetsUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch default material values');
      }
      const data = await response.json();
      // Merge so a re-fetch on toggle refreshes the computed sheet without dropping the bobbin_enabled /
      // filtering flags ChooseLayoutDimensions writes into this object for the combinations POST.
      setMaterialLayoutDimensions((previous) => ({ ...(previous || {}), ...data }));
    } catch (error) {
      console.error('Error fetching default material values:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch default material values from API on mount (part_type default bobbin).
  useEffect(() => {
    fetchDefaultMaterialValues(undefined);
  }, []);

  const navSectionLabel = () => {
    if(props.navSection.label) {
      return <span className={styles.navSectionLabel}> {props.navSection.label} </span>
    }
  }

  const chooseLayoutDimensionsComponent = () => {
    return <ChooseLayoutDimensions
      defaultValues={materialLayoutDimensions}
      publicSetter={setMaterialLayoutDimensions}
      onBobbinChange={(value) => {
        setBobbinEnabled(value);
        fetchDefaultMaterialValues(value);
      }}
      index={props.index}
    />
  }

  // Whether to show the material sheet finder's admin sanity displays (the resolved part_type and the
  // chosen material sheets). Admins always see them; users in ENV['quote_builder_v2_enabled_users'] are
  // treated like admins on this Manufacturing Cost Calculator page for now, so they see them too. The
  // flag is undefined on pages that do not set it, which correctly keeps these displays hidden there.
  const canSeeSectionInsights = window.current_user_admin || window.quote_builder_v2_enabled;

  // Append the section's part_type to its title (e.g. "Corrugated cardboard
  // (corrugated_cardboard_flexo)"), so a reviewer can see exactly which part_type a section resolved to
  // and flag anything wrong. Hidden from customers.
  const adminPartTypeSuffix = () => {
    if (!canSeeSectionInsights || !props.navSection.part_type) {
      return null;
    }

    return (
      <span style={{ fontSize: '14px', color: '#888', fontWeight: 'normal' }}>
        {' '}({props.navSection.part_type})
      </span>
    );
  }

  // A single material-sheet chip (e.g. "B1 707mm X 1000mm" or the "Üretilemiyor" indicator).
  const materialDimensionChip = (text, key) => (
    <div
      key={key}
      style={{
        fontSize: '13px',
        color: '#555',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '4px 12px',
        backgroundColor: '#f9f9f9',
      }}
    >
      {text}
    </div>
  );

  // Sanity check: the material sheet dimensions computed for this section are hidden from customers,
  // but shown to admins (and quote-builder-v2 users, per canSeeSectionInsights) next to the section
  // heading so they can eyeball the chosen sheets and flag anything that looks off. The sheets are
  // grouped by sub_section, titled by its label (the same "Cover" / "Tray" / "Tray & Cover" the section's
  // tabs use), so a section with an oversize combined nesting still shows the producible per-part sheets
  // instead of one whole-section "cannot produce". Falls back to the flat list for any endpoint that does
  // not send groups. Nothing renders when the viewer is not permitted or when nothing was computed.
  const adminChosenMaterialDimensionsLine = () => {
    if (!canSeeSectionInsights) {
      return null;
    }

    const groups = materialLayoutDimensions?.material_sheet_groups || [];
    const flatSheets = materialLayoutDimensions?.layout_dimensions || [];
    const renderableGroups = groups.filter((group) => group.dimensions && group.dimensions.length > 0);
    const hasGroups = renderableGroups.length > 0;

    // NOTHING WAS COMPUTED. Two situations reach here and must read differently: the fetch has not answered
    // yet, and it answered with no sheets. A blank space where the dimensions belong looks like the page
    // errored, so once the fetch has actually ANSWERED (we are no longer loading and hold a response) say so
    // plainly -- an empty result is a legitimate answer (e.g. no bobbin width holds the dieline), not a
    // failure. While it is still loading, stay quiet. /mcc only: the legacy page's empty state is left as it
    // was.
    if (!hasGroups && flatSheets.length === 0) {
      if (!window.frank_mode || isLoading || !materialLayoutDimensions) {
        return null;
      }

      return (
        <center>
          <div
            style={{ marginTop: '-10px', marginBottom: '10px', fontSize: '12px', color: '#888' }}
            title="No material sheet was found for this section's dieline -- an empty result, not an error."
          >
            Material sheets cannot be found.
          </div>
        </center>
      );
    }

    return (
      <center>
        <div
          style={{ marginTop: '-10px', marginBottom: '10px' }}
          title="The material sheet dimensions computed for this section, per part. Flag if anything looks off."
        >
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px' }}>
            Chosen material dimensions:
          </div>

          {hasGroups ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              {renderableGroups.map((group, groupIndex) => {
                const partLabel = group.label || (group.part_names?.length ? group.part_names.join(', ') : null);

                return (
                  <div
                    key={partLabel || groupIndex}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}
                  >
                    {partLabel && (
                      <div style={{ fontSize: '12px', color: '#555', fontWeight: 'bold' }}>
                        {partLabel}
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: '6px',
                      }}
                    >
                      {group.dimensions.map((sheet, sheetIndex) => materialDimensionChip(sheet.description, `${sheet.description}-${sheetIndex}`))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
              {flatSheets.map((sheet, sheetIndex) => materialDimensionChip(sheet.description, sheetIndex))}
            </div>
          )}
        </div>
      </center>
    );
  }

  if(props.navSection.part_type == "general") {
    return <GeneralNavSection totalNavSectionSize={props.totalNavSectionSize} />
  }

  // Show loading state while fetching default material values
  if (isLoading || !materialLayoutDimensions) {
    return (
      <div className={styles.LayoutAndQuoteBuilderNavSection}>
        <div className={styles.nav_section}>
          {shouldShowSectionLabel(props.totalNavSectionSize, props.navSection.label) && (
            <center> <h2> { props.navSection.label} </h2> </center>
          )}
          <div style={{ textAlign: 'center', padding: '20px' }}>
            {t('quote_builder.loading')}...
          </div>
        </div>
      </div>
    );
  }

  const sectionBody = () => (
    <>
      { selectedList.length == 0 && <LayoutNavSectionCombinationList
        navSection={props.navSection}
        index={props.index}
        count={props.count}
        setSelectedList={setSelectedList}
        materialLayoutDimensions={materialLayoutDimensions}
      /> }

      <SelectedList selectedList={selectedList} setSelectedList={setSelectedList} config={props.config} index={props.index} totalNavSectionSize={props.totalNavSectionSize}/>
    </>
  );

  return (
    <>
    <div className={styles.LayoutAndQuoteBuilderNavSection}>

      <div className={styles.nav_section}>

        {shouldShowSectionLabel(props.totalNavSectionSize, props.navSection.label) && (
          <center> <h2> { props.navSection.label}{adminPartTypeSuffix()} </h2> </center>
        )}

        {/* The bobbin checkbox is an INPUT to the material-sheet finder, so it sits ABOVE the resulting
            "Chosen material dimensions" line, which recomputes when the checkbox toggles. */}

        <div className={styles.nav_section_layout_selector}> <span> {chooseLayoutDimensionsComponent()} </span> </div>

        {adminChosenMaterialDimensionsLine()}

        {/* Frank lists the manufacturing lines it found under the material sheets (verify view; no selection
            yet). It takes the bobbin value too, so it recomputes over the very sheets shown above. */}
        {window.frank_mode && (
          <ManufacturingLineLists navSectionIndex={props.navSection.index} bobbinEnabled={bobbinEnabled} config={props.config} />
        )}

         <hr />
        {/* Frank mode shows ONLY the section material sheets above; the manufacturing lines + solutions in
            sectionBody() are hidden until Frank rebuilds those services. */}
        { !window.frank_mode && sectionBody() }

      </div>
    </div>
    <br />
    <br />
    </>
  )
}

export default LayoutAndQuoteBuilderNavSection;
